import { useState, useEffect, useRef } from 'react';
import LoadingMessages from '../components/LoadingMessages';
import { apiClient } from '../store/authStore';

const C = {
  bg:       '#0a0a0f',
  surface:  '#13131f',
  surfaceHi:'#181828',
  input:    '#1a1a2e',
  border:   'rgba(124,58,237,0.2)',
  borderHi: 'rgba(124,58,237,0.45)',
  violet:   '#7c3aed',
  blue:     '#3b82f6',
  text:     '#f1f5f9',
  muted:    '#94a3b8',
  faint:    '#475569',
  gradient: 'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  shadow:   '0 4px 24px rgba(124,58,237,0.1)',
  font:     "'Inter', system-ui, -apple-system, sans-serif",
};

const card = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 16,
  padding: '24px 26px',
  marginBottom: 16,
  boxShadow: C.shadow,
};

/* Language config — each with its own accent color */
const LANGS = [
  { v: 'en', l: 'English',  f: '🇬🇧', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.35)'  },
  { v: 'fr', l: 'Français', f: '🇫🇷', color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.35)' },
  { v: 'es', l: 'Español',  f: '🇪🇸', color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.35)'  },
  { v: 'ar', l: 'Arabic',   f: '🇸🇦', color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.35)'  },
];

const LANG_MAP = Object.fromEntries(LANGS.map(l => [l.v, l]));

function LangBadge({ code, small }) {
  const l = LANG_MAP[code?.toLowerCase()] || { color: C.violet, bg: 'rgba(124,58,237,0.12)', border: 'rgba(124,58,237,0.3)', f: '🌐', l: code };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: l.bg, border: `1px solid ${l.border}`, borderRadius: 6,
      padding: small ? '2px 7px' : '4px 10px',
      fontSize: small ? 10 : 11, fontWeight: 700, color: l.color,
      letterSpacing: '0.07em', textTransform: 'uppercase',
    }}>
      {l.f} {code?.toUpperCase()}
    </span>
  );
}

export default function MetaGenerator() {
  const [url, setUrl]         = useState('');
  const [langs, setLangs]     = useState([]);
  const [job, setJob]         = useState(null);
  const [results, setResults] = useState([]);
  const [detected, setDetected] = useState(null);
  const [error, setError]     = useState('');
  const [copied, setCopied]   = useState(null);
  const poll = useRef(null);

  const toggle = (v) => setLangs(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v]);

  const generate = async () => {
    if (!url.trim()) return;
    setError(''); setJob(null); setResults([]); setDetected(null);
    try {
      const body = { url: url.trim() };
      if (langs.length > 0) body.languages = langs;
      const r = await apiClient.post('/traffic/meta/generate-bulk', body);
      if (r.auto_detected && r.detected_languages?.length) setDetected(r.detected_languages[0]);
      setJob({ job_id: r.job_id, status: 'queued', progress: 0, generated: 0, total_pages: 0 });
      startPoll(r.job_id);
    } catch (e) { setError(e.message || 'Generation failed'); }
  };

  const startPoll = (jid) => {
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const st = await apiClient.get(`/traffic/jobs/${jid}`);
        setJob(p => ({ ...p, ...st }));
        if (st.status === 'completed' || st.status === 'failed') {
          clearInterval(poll.current);
          if (st.status === 'completed') loadResults(jid);
        }
      } catch (_) {}
    }, 3000);
  };

  const loadResults = async (jid) => {
    try {
      const d = await apiClient.get(`/traffic/meta/suggestions/${jid}`);
      setResults(d.suggestions || d || []);
    } catch (_) {}
  };

  const copy = (txt, id) => {
    navigator.clipboard.writeText(txt);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const exportCSV = () => {
    const rows = [['URL', 'Title', 'Language', 'Meta Description', 'Chars']];
    results.forEach(r => {
      Object.entries(r.metas || {}).forEach(([lg, meta]) => {
        rows.push([r.url, r.title || '', lg.toUpperCase(), meta, String(meta?.length || 0)]);
      });
    });
    const csv = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'meta-descriptions.csv'; a.click();
  };

  useEffect(() => () => { if (poll.current) clearInterval(poll.current); }, []);

  const running = job && ['queued', 'running'].includes(job.status);
  const flat = results.flatMap((r, ri) =>
    Object.entries(r.metas || {}).map(([lg, meta], li) => ({ r, ri, li, lg, meta, id: `${ri}-${li}` }))
  );

  const charColor = (n) => n > 155 ? '#f87171' : n < 120 ? '#fbbf24' : '#34d399';

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: C.font, color: C.text }}>
      <main style={{ maxWidth: 920, margin: '0 auto', padding: '40px 28px 80px' }}>

        {/* Page header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.25)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✦</div>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.violet, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Meta Generator</span>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: '0 0 6px', letterSpacing: '-0.8px' }}>Meta Description Generator</h1>
          <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>AI-powered bulk generation of optimized meta descriptions for every page on your site.</p>
        </div>

        {/* Form card */}
        <div style={card}>
          <p style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: '0 0 4px' }}>Your site URL</p>
          <p style={{ fontSize: 12, color: C.muted, margin: '0 0 16px' }}>We'll crawl every page and generate an optimized meta description.</p>

          <div style={{ display: 'flex', gap: 10, marginBottom: 22 }}>
            <input
              style={{ flex: 1, background: C.input, border: `1px solid rgba(124,58,237,0.3)`, borderRadius: 10, padding: '11px 14px', fontSize: 14, color: C.text, outline: 'none', fontFamily: C.font, transition: 'border-color 0.2s' }}
              placeholder="https://example.com"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && generate()}
              onFocus={e => e.target.style.borderColor = C.violet}
              onBlur={e => e.target.style.borderColor = 'rgba(124,58,237,0.3)'}
            />
            <button onClick={generate} disabled={running || !url.trim()}
              style={{ padding: '11px 22px', background: (running || !url.trim()) ? C.faint : C.gradient, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: (running || !url.trim()) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: C.font, boxShadow: (!running && url.trim()) ? '0 4px 16px rgba(124,58,237,0.3)' : 'none', transition: 'all 0.15s' }}>
              {running ? '⟳ Generating…' : '✦ Generate Metas'}
            </button>
          </div>

          {/* Language selector */}
          <div style={{ borderTop: `1px solid rgba(255,255,255,0.06)`, paddingTop: 18 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: C.faint, margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Languages <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: C.faint }}>— leave empty for auto-detect</span>
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {LANGS.map(lg => {
                const on = langs.includes(lg.v);
                return (
                  <button key={lg.v} onClick={() => toggle(lg.v)} style={{
                    display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px',
                    borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    background: on ? lg.bg : 'rgba(255,255,255,0.04)',
                    border: `1.5px solid ${on ? lg.border : 'rgba(255,255,255,0.08)'}`,
                    color: on ? lg.color : C.muted,
                    transition: 'all 0.15s', fontFamily: C.font,
                    boxShadow: on ? `0 0 12px ${lg.bg}` : 'none',
                  }}>
                    <span style={{ fontSize: 15 }}>{lg.f}</span>
                    {lg.l}
                    {on && <span style={{ fontSize: 11, background: lg.color, color: '#fff', borderRadius: 4, padding: '1px 5px', fontWeight: 800 }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {detected && (
            <div style={{ marginTop: 14 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 20, padding: '4px 12px', fontSize: 11, color: '#34d399', fontWeight: 700 }}>
                🌐 Auto-detected: <strong>{detected.toUpperCase()}</strong>
              </span>
            </div>
          )}

          {error && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: 12, color: '#f87171' }}>
              {error}
            </div>
          )}
        </div>

        {/* Progress card */}
        {job && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: job.status === 'completed' ? '#34d399' : job.status === 'failed' ? '#f87171' : C.text }}>
                {job.status === 'queued' ? '⏳ Queued…' : job.status === 'running' ? '⚙️ Generating…' : job.status === 'completed' ? '✓ Complete' : '✗ Failed'}
              </span>
              <span style={{ fontSize: 12, color: C.muted }}>{job.generated || 0} / {job.total_pages || '?'} pages</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 99, overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${job.progress || 0}%`, background: C.gradient, borderRadius: 99, transition: 'width 0.5s ease', boxShadow: '0 0 12px rgba(124,58,237,0.5)' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: C.faint }}>
              <span style={{ fontWeight: 700, color: C.violet }}>{job.progress || 0}%</span>
              {job.skipped > 0 && <span style={{ color: C.muted }}>{job.skipped} skipped</span>}
              {job.errors  > 0 && <span style={{ color: '#f87171' }}>{job.errors} errors</span>}
            </div>
            {running && <LoadingMessages style={{ marginTop: 16 }} />}
          </div>
        )}

        {/* Results card */}
        {flat.length > 0 && (
          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: C.text, margin: '0 0 2px' }}>Results</p>
                <p style={{ fontSize: 12, color: C.muted, margin: 0 }}>{results.length} pages · {flat.length} meta descriptions</p>
              </div>
              <button onClick={exportCSV}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(124,58,237,0.1)', border: `1px solid rgba(124,58,237,0.3)`, borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#a78bfa', cursor: 'pointer', fontFamily: C.font }}>
                ↓ Export CSV
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Path', 'Lang', 'Meta Description', 'Chars', ''].map((h, i) => (
                      <th key={i} style={{ textAlign: 'left', fontSize: 10, color: C.faint, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '10px 14px', borderBottom: `1px solid rgba(255,255,255,0.06)` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {flat.map(({ r, lg, meta, id }) => (
                    <tr key={id} style={{ transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '11px 14px', borderBottom: `1px solid rgba(255,255,255,0.04)`, fontSize: 11, color: C.muted, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.url.replace(/^https?:\/\/[^/]+/, '') || '/'}
                      </td>
                      <td style={{ padding: '11px 14px', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                        <LangBadge code={lg} small />
                      </td>
                      <td style={{ padding: '11px 14px', borderBottom: `1px solid rgba(255,255,255,0.04)`, fontSize: 13, color: C.text, maxWidth: 360, lineHeight: 1.5 }}>{meta}</td>
                      <td style={{ padding: '11px 14px', borderBottom: `1px solid rgba(255,255,255,0.04)`, color: charColor(meta?.length || 0), fontWeight: 700, fontSize: 13 }}>
                        {meta?.length || 0}
                      </td>
                      <td style={{ padding: '11px 14px', borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                        <button onClick={() => copy(meta, id)}
                          style={{ background: copied === id ? 'rgba(52,211,153,0.15)' : 'rgba(124,58,237,0.1)', border: `1px solid ${copied === id ? 'rgba(52,211,153,0.3)' : 'rgba(124,58,237,0.25)'}`, borderRadius: 6, padding: '4px 12px', fontSize: 11, color: copied === id ? '#34d399' : '#a78bfa', cursor: 'pointer', fontFamily: C.font, fontWeight: 600, transition: 'all 0.2s' }}>
                          {copied === id ? '✓ Copied' : 'Copy'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Completed but no results */}
        {job?.status === 'completed' && flat.length === 0 && (
          <div style={{ ...card, textAlign: 'center', padding: '48px 32px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#34d399', margin: '0 0 6px' }}>All pages already optimized!</p>
            <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>No pages found with missing or short meta descriptions.</p>
          </div>
        )}

        {/* Empty state */}
        {!job && flat.length === 0 && (
          <div style={{ background: C.surface, border: `1.5px dashed ${C.border}`, borderRadius: 16, padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, background: 'rgba(124,58,237,0.1)', border: `1px solid rgba(124,58,237,0.2)`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, margin: '0 auto 16px' }}>✦</div>
            <p style={{ fontSize: 16, fontWeight: 700, color: C.muted, margin: '0 0 6px' }}>Ready to generate</p>
            <p style={{ fontSize: 13, color: C.faint, margin: 0 }}>Enter your site URL above and choose your languages to start.</p>
          </div>
        )}
      </main>
    </div>
  );
}
